import React from "react";
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  Typography,
  useTheme,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/AddOutlined";
import BookmarkIcon from "@mui/icons-material/BookmarkBorderOutlined";
import { scopeReady } from "../../shared/navigationRegistry";

// Search results for the Dashboard launchpad. Each result is its own glass
// container, mirroring the boxed nav items, so the search view and the menu
// view read the same. Keyboard nav (arrows + Enter) is driven by the parent
// through the focusedIndex / ref / handler props.
export default function DashboardSearchResults({
  results,
  focusedIndex,
  setItemRef,
  onFocus,
  onKeyDown,
  onActivate,
  onAddBookmark,
  bookmarkedIds = [],
  ctx,
}) {
  const theme = useTheme();

  if (results.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
        <Typography sx={{ fontSize: 14 }}>No matches</Typography>
      </Box>
    );
  }

  return (
    <List
      role="listbox"
      aria-label="Search results"
      onKeyDown={onKeyDown}
      sx={{ p: 0, display: "flex", flexDirection: "column", gap: 1 }}
    >
      {results.map((entry, i) => {
        const { node, breadcrumb } = entry;
        const ready = scopeReady(node, ctx);
        const focused = focusedIndex === i;
        const added = bookmarkedIds.includes(node.id);
        const lastIdx = breadcrumb.length - 1;
        return (
          <Box
            key={node.id}
            sx={{ ...theme.custom.glassBox, overflow: "hidden" }}
          >
            <ListItemButton
              ref={(el) => setItemRef(i, el)}
              role="option"
              tabIndex={focused ? 0 : -1}
              aria-selected={focused}
              aria-disabled={!ready}
              onClick={() => onActivate(node)}
              onFocus={() => onFocus(i)}
              sx={{
                borderRadius: 0,
                py: 1,
                px: 1.5,
                opacity: ready ? 1 : 0.5,
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: "-2px",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  rowGap: 0.25,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {breadcrumb.map((seg, idx) => {
                  const isLeaf = idx === lastIdx;
                  return (
                    <React.Fragment key={idx}>
                      {idx > 0 && (
                        <ChevronRightIcon
                          fontSize="small"
                          sx={{ color: "text.disabled", mx: 0.25 }}
                        />
                      )}
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 14,
                          fontWeight: isLeaf ? 600 : 400,
                          color: isLeaf ? "text.primary" : "text.secondary",
                        }}
                      >
                        {seg}
                      </Typography>
                    </React.Fragment>
                  );
                })}
              </Box>
              <IconButton
                size="small"
                disabled={added}
                aria-label={
                  added
                    ? `${node.label} already bookmarked`
                    : `Add ${node.label} bookmark`
                }
                onClick={(e) => {
                  e.stopPropagation();
                  onAddBookmark(node.id);
                }}
                sx={{ ml: 1 }}
              >
                {added ? (
                  <BookmarkIcon sx={{ fontSize: 20, color: "#00000080" }} />
                ) : (
                  <AddIcon sx={{ fontSize: 20, color: "primary.main" }} />
                )}
              </IconButton>
            </ListItemButton>
          </Box>
        );
      })}
    </List>
  );
}
